import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_BASE_URL } from '../services/api';
import { useStore } from '../store';
import { Check, ChevronDown, Search } from 'lucide-react';
import {
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@roger/ui-components';

interface Organization {
  id: string;
  name: string;
}

interface AgencySwitcherProps {
  /** Custom class name for styling */
  className?: string;
}

/**
 * AgencySwitcher component that allows users to switch between different agencies (organizations).
 * Uses a custom switcher UI that fetches organizations from the backend.
 *
 * Note: WorkOS's built-in OrganizationSwitcher widget requires CORS configuration in the
 * WorkOS Dashboard (Authentication > Cross-Origin Resource Sharing). If you want to use
 * the built-in widget, add your frontend URL to the allowed origins there.
 */
const AgencySwitcher: React.FC<AgencySwitcherProps> = ({ className }) => {
  const user = useStore(state => state.user);
  const [isLoading, setIsLoading] = useState(false);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isFetchingOrgs, setIsFetchingOrgs] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Filter organizations based on search query
  const filteredOrganizations = useMemo(() => {
    if (!searchQuery.trim()) {
      return organizations;
    }
    const query = searchQuery.toLowerCase();
    return organizations.filter(org => org.name.toLowerCase().includes(query));
  }, [organizations, searchQuery]);

  // Reset search when popover closes
  useEffect(() => {
    if (!isPopoverOpen) {
      setSearchQuery('');
    }
  }, [isPopoverOpen]);

  // Fetch user's organizations
  useEffect(() => {
    const fetchOrganizations = async () => {
      setIsFetchingOrgs(true);
      try {
        const response = await fetch(`${API_BASE_URL}/auth/organizations`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          setOrganizations(data.organizations || []);
        }
      } catch (error) {
        console.error('Error fetching organizations:', error);
      } finally {
        setIsFetchingOrgs(false);
      }
    };

    if (user?.agency) {
      fetchOrganizations();
    } else {
      setIsFetchingOrgs(false);
    }
  }, [user?.agency]);

  /**
   * Handles switching to a different organization.
   * Calls the backend to get a redirect URL for re-authentication.
   */
  const handleSwitchOrganization = useCallback(
    async (organizationId: string) => {
      setIsLoading(true);
      setIsPopoverOpen(false);
      try {
        const response = await fetch(
          `${API_BASE_URL}/auth/switch-organization`,
          {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ organization_id: organizationId }),
          }
        );

        if (!response.ok) {
          throw new Error('Failed to switch organization');
        }

        const data = await response.json();

        if (data.redirect_url) {
          // Clear local storage before redirecting to ensure clean state
          localStorage.removeItem('roger-emr-store');
          // Redirect to WorkOS to re-authenticate with the new organization
          window.location.href = data.redirect_url;
        } else {
          window.location.reload();
        }
      } catch (error) {
        console.error('Error switching organization:', error);
        setIsLoading(false);
        alert('Failed to switch organization. Please try again.');
      }
    },
    []
  );

  // Don't render if user doesn't have an agency
  if (!user?.agency) {
    return null;
  }
  const agencyName = user.agency.written_name;

  // Loading state while fetching organizations
  if (isFetchingOrgs) {
    return (
      <div
        className={`${className ?? ''} truncate text-sm text-muted-foreground`}
      >
        {agencyName}
      </div>
    );
  }

  // Loading state while switching
  if (isLoading) {
    return (
      <div
        className={`${className ?? ''} truncate text-sm text-muted-foreground`}
      >
        Switching...
      </div>
    );
  }

  // Don't show switcher if user only belongs to one organization
  if (organizations.length <= 1) {
    return (
      <div
        className={`${className ?? ''} truncate text-sm text-muted-foreground`}
      >
        {agencyName}
      </div>
    );
  }

  // Render organization switcher dropdown
  return (
    <div className={className}>
      <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
        <PopoverTrigger asChild>
          <button
            aria-label={`Switch agency, current agency ${agencyName}`}
            className='flex max-w-full items-center gap-1 rounded-sm text-left text-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
          >
            <span className='truncate'>{agencyName}</span>
            <ChevronDown aria-hidden='true' className='h-3 w-3 shrink-0' />
          </button>
        </PopoverTrigger>
        <PopoverContent align='start' className='w-72 p-2'>
          <div className='text-xs font-semibold text-muted-foreground uppercase tracking-wider px-2 py-1.5 mb-1'>
            Switch Agency
          </div>
          <div className='relative mb-2'>
            <Search className='absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
            <Input
              type='text'
              placeholder='Search agencies...'
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className='pl-8'
            />
          </div>
          <div className='space-y-0.5 max-h-64 overflow-y-auto'>
            {filteredOrganizations.length === 0 ? (
              <div className='px-2 py-3 text-sm text-muted-foreground text-center'>
                No agencies found
              </div>
            ) : (
              filteredOrganizations.map(org => {
                const isCurrentOrg = org.name === agencyName;
                return (
                  <button
                    key={org.id}
                    onClick={() =>
                      !isCurrentOrg && handleSwitchOrganization(org.id)
                    }
                    disabled={isLoading || isCurrentOrg}
                    className={`w-full text-left px-2 py-2 rounded-md text-sm transition-colors flex items-center justify-between ${
                      isCurrentOrg
                        ? 'bg-primary/10 text-primary cursor-default'
                        : 'hover:bg-accent cursor-pointer'
                    }`}
                  >
                    <div className='flex items-center'>
                      <span className={isCurrentOrg ? 'font-medium' : ''}>
                        {org.name}
                      </span>
                    </div>
                    {isCurrentOrg && <Check className='h-4 w-4 text-primary' />}
                  </button>
                );
              })
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default AgencySwitcher;
